import React, { useState, useEffect } from 'react';
import apiClient from '../../utils/apiClient';
import { toast } from 'sonner';
import { Plus, Edit, Trash2, BookOpen, Users as UsersIcon } from 'lucide-react';
import { useAuth } from '@/App';

const ClassManagement = () => {
  const [classes, setClasses] = useState([]);
  const [wardens, setWardens] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [editingClass, setEditingClass] = useState(null);
  
  const [formData, setFormData] = useState({
    name: '',
    sections: [],
    sectionInput: '',
    assigned_warden_id: '',
    academic_year: new Date().getFullYear() + '-' + (new Date().getFullYear() + 1)
  });

  const {user} = useAuth();

  useEffect(() => {
    fetchClasses();
    fetchWardens();
  }, []);

  const fetchClasses = async () => {
    setLoading(true);
    try {
      const response = await apiClient.get(`/v1/admin/classes/?hostel_id=${user.hostel_id}`);
      // Backend returns array directly, not wrapped in object
      setClasses(Array.isArray(response.data) ? response.data : []);
    } catch (error) {
      toast.error('Failed to load classes');
    } finally {
      setLoading(false);
    }
  };

  const fetchWardens = async () => {
    try {
      const response = await apiClient.get(`/v1/admin/wardens/?hostel_id=${user.hostel_id}`);
      // Backend returns array directly
      setWardens(Array.isArray(response.data) ? response.data : []);
    } catch (error) {
      toast.error('Failed to load wardens:', error);
    }
  };

  const handleOpenModal = (cls = null) => {
    if (cls) {
      setEditingClass(cls);
      setFormData({
        name: cls.name,
        sections: cls.sections || [],
        sectionInput: '',
        assigned_warden_id: cls.assigned_warden_id || '',
        academic_year: cls.academic_year || ''
      });
    } else {
      setEditingClass(null);
      setFormData({
        name: '',
        sections: [],
        sectionInput: '',
        assigned_warden_id: '',
        academic_year: new Date().getFullYear() + '-' + (new Date().getFullYear() + 1)
      });
    }
    setShowModal(true);
  };

  const handleAddSection = () => {
    if (formData.sectionInput.trim()) {
      setFormData({
        ...formData,
        sections: [...formData.sections, formData.sectionInput.toUpperCase()],
        sectionInput: ''
      });
    }
  };

  const handleRemoveSection = (section) => {
    setFormData({
      ...formData,
      sections: formData.sections.filter(s => s !== section)
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    
    try {
      const payload = {
        name: formData.name,
        sections: formData.sections,
        assigned_warden_id: formData.assigned_warden_id || null,
        academic_year: formData.academic_year
      };

      if (editingClass) {
        await apiClient.put(`/v1/admin/classes/${editingClass.id}`, payload);
        toast.success('Class updated successfully');
      } else {
        await apiClient.post('/v1/admin/classes/', {
          ...payload,
          hostel_id: user.hostel_id
        });
        toast.success('Class created successfully');
      }
      
      setShowModal(false);
      fetchClasses();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Operation failed');
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (classId, studentCount) => {
    if (studentCount > 0) {
      toast.error(`Cannot delete class with ${studentCount} enrolled student(s). Move or remove students first.`);
      return;
    }
    
    if (!window.confirm('Are you sure you want to delete this class?')) return;
    
    try {
      await apiClient.delete(`/v1/admin/classes/${classId}`);
      toast.success('Class deleted successfully');
      fetchClasses();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete class');
    }
  };

  return (
    <div className="p-6 max-w-7xl mx-auto">
      <div className="flex justify-between items-center mb-6">
        <h1 className="text-3xl font-bold">Class Management</h1>
        <button
          onClick={() => handleOpenModal()}
          className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
        >
          <Plus className="w-4 h-4" />
          Add Class
        </button>
      </div>

      {/* Classes Grid */}
      {loading ? (
        <div className="text-center py-8">Loading...</div>
      ) : (
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
          {classes.map((cls) => (
            <div key={cls.id} className="bg-white rounded-lg shadow-md p-6">
              <div className="flex items-start justify-between mb-4">
                <div className="flex items-center gap-3">
                  <div className="w-12 h-12 bg-blue-100 rounded-full flex items-center justify-center">
                    <BookOpen className="w-6 h-6 text-blue-600" />
                  </div>
                  <div>
                    <h3 className="text-lg font-semibold text-gray-900">{cls.name}</h3>
                    <p className="text-sm text-gray-500">{cls.academic_year}</p>
                  </div>
                </div>
              </div>

              {/* Sections */}
              {cls.sections && cls.sections.length > 0 && (
                <div className="mb-3">
                  <p className="text-sm text-gray-600 mb-2">Sections:</p>
                  <div className="flex flex-wrap gap-2">
                    {cls.sections.map(section => (
                      <span key={section} className="px-2 py-1 bg-purple-100 text-purple-700 rounded text-sm">
                        {section}
                      </span>
                    ))}
                  </div>
                </div>
              )}

              {/* Student Count */}
              <div className="flex items-center gap-2 text-sm text-gray-700 mb-3">
                <UsersIcon className="w-4 h-4" />
                <span>{cls.student_count || 0} Students</span>
              </div>

              {/* Warden */}
              {cls.warden_info && (
                <div className="mb-4 text-sm">
                  <span className="text-gray-600">Warden: </span>
                  <span className="font-medium">{cls.warden_info.name}</span>
                </div>
              )}

              {/* Actions */}
              <div className="flex gap-2">
                <button
                  onClick={() => handleOpenModal(cls)}
                  className="flex-1 flex items-center justify-center gap-1 px-3 py-2 bg-blue-50 text-blue-600 rounded hover:bg-blue-100"
                >
                  <Edit className="w-4 h-4" />
                  Edit
                </button>
                <button
                  onClick={() => handleDelete(cls.id, cls.student_count)}
                  className="flex-1 flex items-center justify-center gap-1 px-3 py-2 bg-red-50 text-red-600 rounded hover:bg-red-100"
                >
                  <Trash2 className="w-4 h-4" />
                  Delete
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
      {!loading && classes.length === 0 && <p className="text-center py-4">No classes found</p>}


      {/* Add/Edit Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg max-w-2xl w-full max-h-[90vh] overflow-y-auto p-6">
            <h2 className="text-2xl font-bold mb-4">
              {editingClass ? 'Edit Class' : 'Add New Class'}
            </h2>

            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Class Name *</label>
                <input
                  type="text"
                  required
                  placeholder="e.g., Class 6, Grade 7"
                  className="w-full p-2 border border-gray-300 rounded-lg"
                  value={formData.name}
                  onChange={(e) => setFormData({...formData, name: e.target.value})}
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Sections (Optional)</label>
                <div className="flex gap-2 mb-2">
                  <input
                    type="text"
                    placeholder="Enter section (e.g., A, B, C)"
                    maxLength="3"
                    className="flex-1 p-2 border border-gray-300 rounded-lg"
                    value={formData.sectionInput}
                    onChange={(e) => setFormData({...formData, sectionInput: e.target.value})}
                    onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), handleAddSection())}
                  />
                  <button
                    type="button"
                    onClick={handleAddSection}
                    className="px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700"
                  >
                    Add
                  </button>
                </div>
                <div className="flex flex-wrap gap-2">
                  {formData.sections.map(section => (
                    <span key={section} className="px-3 py-1 bg-purple-100 text-purple-700 rounded-full flex items-center gap-2">
                      {section}
                      <button
                        type="button"
                        onClick={() => handleRemoveSection(section)}
                        className="text-purple-900 hover:text-purple-600"
                      >
                        ×
                      </button>
                    </span>
                  ))}
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Academic Year *</label>
                <input
                  type="text"
                  required
                  placeholder="2024-2025"
                  className="w-full p-2 border border-gray-300 rounded-lg"
                  value={formData.academic_year}
                  onChange={(e) => setFormData({...formData, academic_year: e.target.value})}
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Assign Warden (Optional)</label>
                <select
                  className="w-full p-2 border border-gray-300 rounded-lg"
                  value={formData.assigned_warden_id}
                  onChange={(e) => setFormData({...formData, assigned_warden_id: e.target.value})}
                >
                  <option value="">No Warden</option>
                  {wardens.map(warden => (
                    <option key={warden.id} value={warden.id}>
                      {warden.name}
                    </option>
                  ))}
                </select>
              </div>

              <div className="flex gap-3 pt-4">
                <button
                  type="button"
                  onClick={() => setShowModal(false)}
                  className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={loading}
                  className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:bg-gray-400"
                >
                  {loading ? 'Saving...' : editingClass ? 'Update' : 'Create'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default ClassManagement;